import { useCallback, useEffect, useState } from 'react'
import {
  ActionIcon,
  Badge,
  Box,
  Group,
  Paper,
  ScrollArea,
  Stack,
  Text,
  UnstyledButton,
} from '@mantine/core'
import { IconMessage, IconRefresh, IconX } from '@/components/icons'
import { HighlightedSearchText } from '@/components/knowledge/HighlightedSearchText'
import { SimilarTicketThreadModal } from '@/components/tickets/SimilarTicketThreadModal'
import { EmptyState, FloatingWorkspacePanel, PageCenterLoader } from '@/components/ui'
import { api } from '@/lib/api'
import { formatDateTimeDot } from '@/lib/ticket-display'
import type { SimilarClosedTicketItem, SimilarClosedTicketsResponse } from '@/types/api'

type Props = {
  ticketId: string
  operatorId?: string | null
  projectName?: string | null
  opened: boolean
  onOpenedChange: (opened: boolean) => void
  onInsertToReply?: (text: string) => void
}

function anchorLabel(anchor?: string | null): string {
  if (anchor === 'second_last') return 'по предпоследнему сообщению клиента'
  if (anchor === 'last') return 'по последнему сообщению клиента'
  return ''
}

export function SimilarClosedTicketsPanel({
  ticketId,
  operatorId,
  projectName,
  opened,
  onOpenedChange,
  onInsertToReply,
}: Props) {
  const setOpened = onOpenedChange
  const [loading, setLoading] = useState(false)
  const [data, setData] = useState<SimilarClosedTicketsResponse | null>(null)
  const [threadTicket, setThreadTicket] = useState<SimilarClosedTicketItem | null>(null)
  const panelStorageKey = `similarClosedTicketsPanelRect:${operatorId || 'anonymous'}:${ticketId || 'unknown'}`

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const resp = await api.ticketSimilarClosed(ticketId, 15)
      setData(resp)
    } catch {
      setData({ items: [], anchor_used: null, anchor_preview: null })
    } finally {
      setLoading(false)
    }
  }, [ticketId])

  useEffect(() => {
    if (!opened || data) return
    void load()
  }, [opened, load, data])

  useEffect(() => {
    setData(null)
    setThreadTicket(null)
  }, [ticketId])

  const highlightQuery = data?.anchor_preview || ''

  const closeAfterThreadAction = useCallback(() => {
    setThreadTicket(null)
    setOpened(false)
  }, [setOpened])

  const handleInsertToReply = useCallback(
    (text: string) => {
      onInsertToReply?.(text)
      closeAfterThreadAction()
    },
    [onInsertToReply, closeAfterThreadAction],
  )

  return (
    <>
      {opened ? (
        <FloatingWorkspacePanel
          storageKey={panelStorageKey}
          opened={opened}
          onClose={() => setOpened(false)}
          defaultWidth={560}
          defaultHeight={720}
          minWidth={420}
          minHeight={460}
          icon={
            <Box
              style={{
                width: 38,
                height: 38,
                borderRadius: 'var(--mantine-radius-lg)',
                display: 'grid',
                placeItems: 'center',
                color: 'var(--mantine-color-blue-6)',
                background: 'var(--mantine-color-blue-light)',
              }}
            >
              <IconMessage size={21} stroke={1.7} />
            </Box>
          }
          title="Похожие обращения"
          subtitle={projectName ? `${projectName} · только закрытые` : 'Закрытые обращения с похожим контекстом'}
          actions={
            <>
              <ActionIcon variant="subtle" onClick={() => void load()} aria-label="Обновить">
                  <IconRefresh size={16} stroke={1.5} />
                </ActionIcon>
              <ActionIcon variant="subtle" onClick={() => setOpened(false)} aria-label="Закрыть">
                  <IconX size={18} stroke={1.5} />
                </ActionIcon>
            </>
          }
        >

            {data?.anchor_preview ? (
              <Box px="lg" py="md" pb="xs">
                <Text size="xs" c="dimmed" mb={4}>
                  Ищем {anchorLabel(data.anchor_used)}:
                </Text>
                <Paper
                  withBorder
                  p="sm"
                  radius="lg"
                  style={{
                    background: 'light-dark(rgba(255,255,255,0.92), rgba(26,27,30,0.78))',
                    borderColor: 'light-dark(rgba(148,163,184,0.24), rgba(255,255,255,0.08))',
                  }}
                >
                  <HighlightedSearchText text={data.anchor_preview} query={data.anchor_preview} size="xs" />
                </Paper>
              </Box>
            ) : null}

            <ScrollArea style={{ flex: 1, minHeight: 0 }} px="lg" py="md" offsetScrollbars type="auto">
              {loading ? (
                <PageCenterLoader />
              ) : !data?.items.length ? (
                <EmptyState
                  title="Похожих закрытых обращений нет"
                  description={
                    data?.anchor_preview
                      ? 'Попробуйте дождаться нового сообщения клиента или открыть другое обращение'
                      : 'Нужно сообщение клиента достаточной длины для сравнения'
                  }
                />
              ) : (
                <Stack gap="sm">
                  {data.items.map((item) => (
                    <UnstyledButton
                      key={item.ticket_id}
                      onClick={() => setThreadTicket(item)}
                      w="100%"
                      p="sm"
                      style={{
                        borderRadius: 'var(--mantine-radius-lg)',
                        border: '1px solid light-dark(rgba(148,163,184,0.24), rgba(255,255,255,0.08))',
                        background:
                          'radial-gradient(circle at 100% 0, light-dark(rgba(59,130,246,0.08), rgba(59,130,246,0.10)), transparent 38%), light-dark(rgba(255,255,255,0.96), var(--mantine-color-dark-7))',
                        boxShadow: '0 12px 30px light-dark(rgba(15,23,42,0.06), rgba(0,0,0,0.20))',
                        transition: 'border-color 140ms ease, box-shadow 140ms ease, background 140ms ease',
                      }}
                    >
                      <Group justify="space-between" wrap="nowrap" gap="xs" mb={4}>
                        <Text fw={600} size="sm" lineClamp={1} style={{ flex: 1 }}>
                          #{item.ticket_number} {item.subject}
                        </Text>
                        <Badge size="sm" variant="light" color="green">
                          {Math.round(item.similarity_score * 100)}%
                        </Badge>
                      </Group>
                      <Text size="xs" c="dimmed" mb={4}>
                        {item.last_operator_name ? `Ответил: ${item.last_operator_name}` : 'Оператор не указан'}
                        {item.closed_at ? ` · ${formatDateTimeDot(item.closed_at)}` : ''}
                      </Text>
                      {item.customer_message_preview ? (
                        <HighlightedSearchText
                          text={item.customer_message_preview}
                          query={highlightQuery}
                          size="xs"
                          c="dimmed"
                          lineClamp={3}
                        />
                      ) : null}
                      <Text size="xs" c="brand" mt={6}>
                        Открыть переписку →
                      </Text>
                    </UnstyledButton>
                  ))}
                </Stack>
              )}
            </ScrollArea>
        </FloatingWorkspacePanel>
      ) : null}

      <SimilarTicketThreadModal
        opened={!!threadTicket}
        onClose={() => setThreadTicket(null)}
        ticketId={threadTicket?.ticket_id || ''}
        ticketLabel={
          threadTicket
            ? `#${threadTicket.ticket_number}${threadTicket.subject ? ` · ${threadTicket.subject}` : ''}`
            : ''
        }
        highlightQuery={highlightQuery}
        onInsertToReply={handleInsertToReply}
        onCopy={closeAfterThreadAction}
      />
    </>
  )
}
